import useApi from '@/lib/api/useApi';
import { useState } from 'react';
import useInterval from 'use-interval';
import api from '@/lib/api';
import ApiError from '@/lib/api/ApiError';
import HttpMethod from '@/lib/constants/HttpMethod.enum';
import { FileStatus, type UploadedImage } from '../types';
import { type WritableDraft } from 'immer';

type ImageStatusResponse = {
  fileStatus: FileStatus;
  thumbnailStorageFilename?: string;
  cloudVisionSafeSearchDetectionResult?: string;
};

const REFRESH_MAX_REQUESTS = 20;

export const finalFileStates = [
  FileStatus.ANTIVIRUS_QUARANTINED,
  FileStatus.INAPPROPRIATE_CONTENT_DETECTED,
  FileStatus.READY,
];

export type ImageLocalStateUpdater = (
  image: WritableDraft<UploadedImage | null>,
) => void;

const useImageFileStatusRefreshing = (
  apiEndpoint: string,
  image: UploadedImage,
  updateImageInLocalState: (stateUpdater: ImageLocalStateUpdater) => void,
): void => {
  const { handleError: handleApiError } = useApi();

  const [requestCount, setRequestCount] = useState(0);

  useInterval(async (): Promise<void> => {
    if (
      finalFileStates.includes(image.file.status) ||
      requestCount >= REFRESH_MAX_REQUESTS
    ) {
      return;
    }

    setRequestCount(requestCount => requestCount + 1);

    try {
      const imageStatusResponse =
        await api.get<ImageStatusResponse>(apiEndpoint);

      if (imageStatusResponse.fileStatus === image.file.status) {
        return;
      }

      updateImageInLocalState(
        (image: WritableDraft<UploadedImage | null>): void => {
          if (image === null) {
            return;
          }

          image.file.status = imageStatusResponse.fileStatus;

          if (
            typeof imageStatusResponse.thumbnailStorageFilename !== 'undefined'
          ) {
            image.file.thumbnailStorageFilename =
              imageStatusResponse.thumbnailStorageFilename;
          }

          if (
            typeof imageStatusResponse.cloudVisionSafeSearchDetectionResult !==
            'undefined'
          ) {
            image.file.cloudVisionSafeSearchDetectionResult =
              imageStatusResponse.cloudVisionSafeSearchDetectionResult;
          }
        },
      );
    } catch (error) {
      if (error instanceof ApiError && error.getHttpStatusCode() === 404) {
        // no-op: file was deleted during upload process, or we're looking at
        // a ProofImage in a rubrics portfolio snapshot for which the file
        // has since been deleted
        return;
      }

      handleApiError(error, HttpMethod.GET);
    }
  }, 1000);
};

export default useImageFileStatusRefreshing;
